import { useState, useEffect, useCallback } from "react";
import { PiSortAscendingLight, PiSortDescendingLight } from "react-icons/pi";
import { CiSearch } from "react-icons/ci";
import "react-datepicker/dist/react-datepicker.css";
import { format, subDays, subMonths, startOfMonth, endOfMonth } from "date-fns";
import { FaFileExcel, FaFilePdf, FaPlus, FaMinus } from "react-icons/fa";
import { HiOutlineMail } from "react-icons/hi";
import { jsPDF } from "jspdf";
import * as XLSX from "xlsx";
import "jspdf-autotable";
import axios from "axios";
import emailjs from "@emailjs/browser";
import { BaseUrl } from "../App";
// import { FadeLoader  } from "react-loader-spinner";
// import { Circles } from 'react-spinners';
import { FadeLoader } from 'react-spinners';


function Client_pay_1() {
  const [selectedOption, setSelectedOption] = useState("Today");
  const [startDate, setStartDate] = useState(new Date());
  const [endDate, setEndDate] = useState(new Date());
  const [showCalendars, setShowCalendars] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [tableData, setTableData] = useState([]);
  const [sortConfig, setSortConfig] = useState({ key: "", direction: "" });
  const [searchQuery, setSearchQuery] = useState("");
  const [entriesPerPage, setEntriesPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedRows, setExpandedRows] = useState({});
  const [screenWidth, setScreenWidth] = useState(window.innerWidth);

  // Handle screen resize
  useEffect(() => {
    const handleResize = () => setScreenWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    let timer;
    if (error) {
      timer = setTimeout(() => {
        setError("");
      }, 5000); // 5 seconds
    }
    return () => clearTimeout(timer);
  }, [error]);
  const handleSelect = (option) => {
    setSelectedOption(option);
    let start, end;
    const today = new Date();

    switch (option) {
      case "Today":
        start = end = today;
        break;
      case "Yesterday":
        start = end = subDays(today, 1);
        break;
      case "Last 7 Days":
        start = subDays(today, 7);
        end = today;
        break;
      case "Last 30 Days":
        start = subDays(today, 30);
        end = today;
        break;
      case "This Month":
        start = startOfMonth(today);
        end = endOfMonth(today);
        break;
      case "Last Month":
        start = startOfMonth(subMonths(today, 1));
        end = endOfMonth(subMonths(today, 1));
        break;
      case "Custom Range":
        setShowCalendars(true);
        return;
      default:
        start = end = today;
    }

    setStartDate(start);
    setEndDate(end);
    setShowCalendars(false);
  };

  const fetchData = useCallback(
    async (retryCount = 0) => {
      try {
        setLoading(true);
        setError(null);
        const response = await axios.post(`${BaseUrl}/backend/api/payment_by_user_type.php`, {
          action: "get_all_payments",
          api_key: "1",
          page: currentPage - 1,
        });
        console.log(response.data)

        if (response.data.status === "true" && Array.isArray(response.data.response)) {
          setTableData(response.data.response);
        } else {
          setError("No payments available or invalid response format");
          setTableData([]);
        }
      } catch (err) {
        console.error("Error fetching data:", err);
        if (retryCount < 3) {
          console.log(`Retrying... Attempt ${retryCount + 1}`);
          setTimeout(() => fetchData(retryCount + 1), 1000 * (retryCount + 1));
        } else {
          setError("Network error. Please check your connection and try again.");
          setTableData([]);
        }
      } finally {
        setLoading(false);
      }
    },
    [currentPage]
  );

  const handleSort = (column) => {
    let direction = "asc";
    if (sortConfig.key === column && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key: column, direction });

    const sortedData = [...tableData].sort((a, b) => {
      if (a[column] < b[column]) return direction === "asc" ? -1 : 1;
      if (a[column] > b[column]) return direction === "asc" ? 1 : -1;
      return 0;
    });

    setTableData(sortedData);
  };

  const getSortIcon = (column) => {
    if (sortConfig.key !== column) {
      return <PiSortAscendingLight className="text-gray-500" />;
    }
    return sortConfig.direction === "asc" ? <PiSortAscendingLight /> : <PiSortDescendingLight />;
  };

  const toggleStatus = async (orderId) => {
    setLoading(true);
    try {
      const currentStatus = tableData.find((booking) => booking.order_id === orderId)?.payment_status;

      if (!currentStatus) {
        throw new Error("Booking not found");
      }

      const newStatus = currentStatus === "Paid" ? "Pending" : "Paid";

      const response = await axios.post(`${BaseUrl}/backend/api/payment_by_user_type.php`, {
        action: "update_payment_status",
        api_key: "1",
        order_id: orderId,
        status: newStatus,
      });

      if (response.data && response.data.status === "true") {
        setSuccessMessage("Payment Status Change Successfully!");
        setTableData((prevData) =>
          prevData.map((booking) =>
            booking.order_id === orderId ? { ...booking, payment_status: newStatus } : booking
          )
        );
        alert(`Payment status updated successfully to ${newStatus}`);
      } else {
        throw new Error(response.data?.response || "Failed to update payment status");
      }
    } catch (error) {
      console.error("Error updating payment status:", error);
      alert(`Failed to update payment status: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => setSearchQuery(e.target.value);

  const handleEntriesChange = (e) => {
    setEntriesPerPage(e.target.value === "All" ? "All" : Number.parseInt(e.target.value, 10));
    setCurrentPage(1);
  };

  const filteredData = tableData?.filter((row) =>
    Object.values(row).some((value) =>
      value?.toString()?.toLowerCase().includes(searchQuery.toLowerCase())
    )
  );

  const startIndex = (currentPage - 1) * entriesPerPage;
  const paginatedData =
    entriesPerPage === "All" ? filteredData : filteredData.slice(startIndex, startIndex + entriesPerPage);

    console.log(paginatedData)

  const totalPages = Math.ceil(filteredData.length / entriesPerPage);

  const handlePrevPage = () => setCurrentPage((prev) => Math.max(prev - 1, 1));
  const handleNextPage = () => setCurrentPage((prev) => Math.min(prev + 1, totalPages));
  const handlePageChange = (page) => {
    setCurrentPage(page);
    fetchData();
  };

  const handleExcelExport = () => {
    const columns = [
      "S.No",
      "Order ID",
      "Client Name",
      "Payment Method",
      "Transaction ID",
      "Payment Date",
      "Amount",
      "Discount",
      "Taxes",
      "Net Amount",
      "Partial Amount",
      "Recurrence",
      "Status",
    ];

    const rows = tableData.map((row) => [
      row.id,
      row.order_id,
      row.client_name,
      row.payment_method,
      row.transaction_id,
      row.payment_date,
      row.amount,
      row.discount,
      row.taxes,
      row.net_amount,
      row.partial_amount,
      row.recurrence_status,
      row.payment_status,
    ]);

    const ws = XLSX.utils.aoa_to_sheet([columns, ...rows]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Table Data");
    XLSX.writeFile(wb, "PaymentDetail.xlsx");
  };

  const handlePdfExport = () => {
    const doc = new jsPDF();
    const columns = [
      "S.No",
      "Order ID",
      "Client Name",
      "Payment Method",
      "Transaction ID",
      "Payment Date",
      "Amount",
      "Discount",
      "Taxes",
      "Net Amount",
      "Partial Amount",
      "Recurrence",
      "Status",
    ];
    const rows = tableData.map((row) => [
      row.id,
      row.order_id,
      row.client_name,
      row.payment_method,
      row.transaction_id,
      row.payment_date,
      row.amount,
      row.discount,
      row.taxes,
      row.net_amount,
      row.partial_amount,
      row.recurrence_status,
      row.payment_status,
    ]);

    doc.autoTable({
      head: [columns],
      body: rows,
      startY: 20,
      theme: "grid",
      margin: { horizontal: 10 },
      headStyles: {
        fillColor: [0, 123, 255],
        textColor: 255,
      },
    });

    doc.save("PaymentDetail.pdf");
  };

  const sendMail = async (order_id) => {
    console.log(order_id);
    // setLoading(true);
    // try {
    //   const response = await emailjs.send(
    //     "service_jyesga5",
    //     "template_272d2yt",
    //     {
    //       to_email: clientEmail,
    //       to_name: clientName,
    //       message: `Hello ${clientName}, this is your payment confirmation email.`,
    //     },
    //     "iFU9xRbx6uWKc2Kbg"
    //   );
    //   alert("Email sent successfully!");
    //   console.log("Success:", response);
    // } catch (error) {
    //   alert("Failed to send email.");
    //   console.error("Error sending email:", error);
    // }

    try {
      const response = await axios.post(`${BaseUrl}/backend/api/payment_by_user_type.php`, {
        action: "payment_confirmation",
        api_key: "1",
        order_id: order_id,
      });
      console.log(response.data)
      if (response.data.status === "true") {
        alert("Email sent successfully!");
      } else {
        alert("Failed to send email.");
      }
      
    } catch (error) {
      console.error("Error sending email:", error);
   
    }finally {
      setLoading(false);
    }
  };

  const toggleRow = (orderId) => {
    setExpandedRows((prev) => ({
      ...prev,
      [orderId]: !prev[orderId],
    }));
  };

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // All possible columns
  const allColumns = [
    { key: "order_id", label: "Order ID" },
    // { key: "client_name", label: "Client Name" },
    { key: "payment_method", label: "Payment Method" },
    { key: "payment_date", label: "Payment Date" },
    { key: "net_amount", label: "Net Amount" },
    { key: "transaction_id", label: "Transaction ID" },
    { key: "amount", label: "Amount" },
    { key: "discount", label: "Discount" },
    { key: "taxes", label: "Taxes" },
    { key: "partial_amount", label: "Partial Amount" },
    { key: "recurrence_status", label: "Recurrence" },
    { key: "payment_status", label: "Status" },
    { key: "client_email", label: "Email" },
  ];

  // Dynamically calculate visible and hidden columns based on screen size
  const getColumns = () => {
    const sidebarWidth = 256; // Assuming sidebar width from AdminPanel
    const availableWidth = screenWidth - sidebarWidth; // Adjust based on sidebar state if tracked
    const columnWidth = 150; // Approx width per column in pixels
    const maxColumns = Math.max(3, Math.min(Math.floor(availableWidth / columnWidth), allColumns.length - 1)); // Min 3, max all-1

    return {
      visibleColumns: allColumns.slice(0, maxColumns),
      hiddenColumns: allColumns.slice(maxColumns),
    };
  };

  const { visibleColumns, hiddenColumns } = getColumns();
  console.log(visibleColumns);


  return (
    // <div className="flex items-center justify-center min-h-screen">
    //   {loading ? (
    //     <>
    //       <Circles height="80" width="80" color="grey" ariaLabel="loading" />
    //       <br />
    //       <h1>Please wait...</h1>
    //     </>
    //   ) : (
    //     <div className="min-h-screen bg-white p-4 lg:px-8 xl:px-16 2xl:px-24">
    //               {successMessage && (
    //         <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded relative mb-4">
    //           <span className="block sm:inline">{successMessage}</span>
    //         </div>
    //       )}

    //       {error && (
    //         <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative mb-4">
    //           <span className="block sm:inline">{error}</span>
    //         </div>
    //       )}
    //       <h1 className="text-2xl font-bold p-4 text-center sm:text-left">Client Payments Details</h1>

    //       <div className="flex flex-col space-y-4 sm:space-y-0 sm:flex-row sm:justify-between sm:items-start w-full">
    //         <div className="flex flex-col sm:flex-row items-center space-y-4 sm:space-y-0 sm:space-x-4 ">
    //           <div className="relative w-full sm:w-auto">
    //             <input
    //               type="text"
    //               placeholder="Search..."
    //               value={searchQuery}
    //               onChange={handleSearch}
    //               className="p-3 w-full sm:w-80 rounded-full border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 transition duration-300 pl-10"
    //             />
    //             <CiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500" />
    //           </div>
    //         </div>
    //         <div className="flex flex-col sm:flex-row items-center space-y-4 sm:space-y-0 sm:space-x-4">
    //           <div className="flex space-x-2">
    //             <button
    //               onClick={handleExcelExport}
    //               className="bg-gradient-to-r from-blue-500 to-blue-600 text-white font-semibold text-sm sm:text-base py-2 px-4 rounded-full shadow-lg hover:shadow-xl hover:from-blue-600 hover:to-blue-700 transition-all duration-300 transform hover:-translate-y-1 hover:scale-105 flex items-center gap-2"
    //             >
    //               <FaFileExcel className="text-xl" />
    //               Excel
    //             </button>
    //             <button
    //               onClick={handlePdfExport}
    //               className="bg-gradient-to-r from-red-500 to-red-600 text-white font-semibold text-sm sm:text-base py-2 px-4 rounded-full shadow-lg hover:shadow-xl hover:from-red-600 hover:to-red-700 transition-all duration-300 transform hover:-translate-y-1 hover:scale-105 flex items-center gap-2"
    //             >
    //               <FaFilePdf className="text-xl" />
    //               PDF
    //             </button>
    //           </div>
    //           <div className="flex items-center space-x-2">
    //             <span className="text-sm">Show</span>
    //             <select
    //               value={entriesPerPage}
    //               onChange={handleEntriesChange}
    //               className="p-2 rounded-xl border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
    //             >
    //               <option value={10}>10</option>
    //               <option value={25}>25</option>
    //               <option value={50}>50</option>
    //               <option value="All">All</option>
    //             </select>
    //             <span className="text-sm">entries</span>
    //           </div>
    //         </div>
    //       </div>

    //       <div className="overflow-x-auto mt-6">
    //         <table className="w-full table-auto bg-white border border-gray-300 shadow-md rounded-lg hidden lg:table">
    //           <thead className="bg-blue-100 text-left">
    //             <tr>
    //               <th className="px-4 py-2 border-b w-16">
    //                 <span>#</span>
    //               </th>
    //               {visibleColumns.map((col) => (
    //                 <th
    //                   key={col.key}
    //                   className="px-4 py-2 border-b cursor-pointer min-w-[120px]"
    //                   onClick={() => handleSort(col.key)}
    //                 >
    //                   <div className="flex items-center gap-2">
    //                     {col.label} {getSortIcon(col.key)}
    //                   </div>
    //                 </th>
    //               ))}
    //             </tr>
    //           </thead>
    //           <tbody>
    //             {paginatedData.length > 0 ? (
    //               paginatedData.map((row, index) => (
    //                 <>
    //                   <tr key={row.order_id} className="hover:bg-gray-50 transition-colors duration-200">
    //                     <td className="px-4 py-2 border-b">
    //                       <button
    //                         onClick={() => toggleRow(row.order_id)}
    //                         className="text-blue-600 hover:text-blue-800 transition-colors duration-200"
    //                       >
    //                         {expandedRows[row.order_id] ? <FaMinus /> : <FaPlus />}
    //                       </button>
    //                       <span className="ml-2">{startIndex + index + 1}</span>
    //                     </td>
    //                     {visibleColumns.map((col) => (
    //                       <td key={col.key} className="px-4 py-2 border-b">
    //                         {col.key === "payment_status" ? (
    //                           <div className="flex items-center gap-2">
    //                             <span
    //                               className={`px-2 py-1 rounded-full text-xs font-semibold ${
    //                                 row[col.key] === "Paid"
    //                                   ? "bg-green-100 text-green-700"
    //                                   : "bg-yellow-100 text-yellow-700"
    //                               }`}
    //                             >
    //                               {row[col.key]}
    //                             </span>
    //                             {row[col.key] === "Pending" && (
    //                               <button
    //                                 className="px-2 py-1 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors duration-200"
    //                                 onClick={() => toggleStatus(row.order_id)}
    //                               >
    //                                 Mark Paid
    //                               </button>
    //                             )}
    //                           </div>
    //                         ) : col.key === "client_email" ? (
    //                           <button
    //                             onClick={() => sendMail(row.client_email, row.client_name)}
    //                             className="flex items-center text-blue-600 hover:text-blue-800 transition-colors duration-200"
    //                           >
    //                             <span className="truncate mr-1">{row[col.key]}</span>
    //                             <HiOutlineMail className="text-lg" />
    //                           </button>
    //                         ) : (
    //                           <span className="truncate">{row[col.key]}</span>
    //                         )}
    //                       </td>
    //                     ))}
    //                   </tr>
    //                   {expandedRows[row.order_id] && (
    //                     <tr className="bg-gray-50">
    //                       <td
    //                         colSpan={visibleColumns.length + 1}
    //                         className="px-6 py-4 border-b transition-all duration-300 ease-in-out"
    //                       >
    //                         <div className="bg-white rounded-lg shadow-sm p-4 border border-gray-200">
    //                           <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
    //                             {hiddenColumns.map((col) => (
    //                               <div key={col.key} className="flex flex-col">
    //                                 <span className="text-sm font-semibold text-gray-700">{col.label}</span>
    //                                 {col.key === "payment_status" ? (
    //                                   <div className="flex items-center gap-2 mt-1">
    //                                     <span
    //                                       className={`px-2 py-1 rounded-full text-xs font-semibold ${
    //                                         row[col.key] === "Paid"
    //                                           ? "bg-green-100 text-green-700"
    //                                           : "bg-yellow-100 text-yellow-700"
    //                                       }`}
    //                                     >
    //                                       {row[col.key]}
    //                                     </span>
    //                                     {row[col.key] === "Pending" && (
    //                                       <button
    //                                         className="px-2 py-1 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors duration-200"
    //                                         onClick={() => toggleStatus(row.order_id)}
    //                                       >
    //                                         Mark Paid
    //                                       </button>
    //                                     )}
    //                                   </div>
    //                                 ) : col.key === "client_email" ? (
    //                                   <button
    //                                     onClick={() => sendMail(row.client_email, row.client_name)}
    //                                     className="flex items-center text-blue-600 hover:text-blue-800 transition-colors duration-200 mt-1"
    //                                   >
    //                                     <span className="truncate mr-1">{row[col.key]}</span>
    //                                     <HiOutlineMail className="text-lg" />
    //                                   </button>
    //                                 ) : (
    //                                   <span className="text-gray-600 mt-1">{row[col.key]}</span>
    //                                 )}
    //                               </div>
    //                             ))}
    //                           </div>
    //                         </div>
    //                       </td>
    //                     </tr>
    //                   )}
    //                 </>
    //               ))
    //             ) : (
    //               <tr>
    //                 <td colSpan={visibleColumns.length + 1} className="text-center py-4 text-gray-500">
    //                   No data available
    //                 </td>
    //               </tr>
    //             )}
    //           </tbody>
    //         </table>

    //         {/* Mobile View */}
    //         <div className="lg:hidden">
    //           {paginatedData.length > 0 ? (
    //             paginatedData.map((row, index) => (
    //               <div
    //                 key={row.order_id}
    //                 className="bg-white border border-gray-300 shadow-md rounded-lg mb-4 p-4"
    //               >
    //                 <div className="flex justify-between items-center mb-3">
    //                   <div className="font-bold text-lg flex items-center">
    //                     <button
    //                       onClick={() => toggleRow(row.order_id)}
    //                       className="text-blue-600 hover:text-blue-800 mr-2 transition-colors duration-200"
    //                     >
    //                       {expandedRows[row.order_id] ? <FaMinus /> : <FaPlus />}
    //                     </button>
    //                     Order ID: {row.order_id}
    //                   </div>
    //                   <span className="text-gray-500">{row.payment_method}</span>
    //                 </div>
    //                 <div className="grid grid-cols-1 gap-3">
    //                   {visibleColumns.slice(1).map((col) => (
    //                     <div key={col.key} className="flex flex-col">
    //                       <span className="font-semibold text-gray-700">{col.label}:</span>
    //                       <span className="text-gray-600">{row[col.key]}</span>
    //                     </div>
    //                   ))}
    //                 </div>
    //                 {expandedRows[row.order_id] && (
    //                   <div className="mt-4 bg-gray-50 p-4 rounded-lg shadow-inner">
    //                     <div className="grid grid-cols-1 gap-3">
    //                       {hiddenColumns.map((col) => (
    //                         <div key={col.key} className="flex flex-col">
    //                           <span className="font-semibold text-gray-700">{col.label}:</span>
    //                           {col.key === "payment_status" ? (
    //                             <div className="flex items-center gap-2 mt-1">
    //                               <span
    //                                 className={`px-2 py-1 rounded-full text-xs font-semibold ${
    //                                   row[col.key] === "Paid"
    //                                     ? "bg-green-100 text-green-700"
    //                                     : "bg-yellow-100 text-yellow-700"
    //                                 }`}
    //                               >
    //                                 {row[col.key]}
    //                               </span>
    //                               {row[col.key] === "Pending" && (
    //                                 <button
    //                                   className="px-2 py-1 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors duration-200"
    //                                   onClick={() => toggleStatus(row.order_id)}
    //                                 >
    //                                   Mark Paid
    //                                 </button>
    //                               )}
    //                             </div>
    //                           ) : col.key === "client_email" ? (
    //                             <button
    //                               onClick={() => sendMail(row.client_email, row.client_name)}
    //                               className="flex items-center text-blue-600 hover:text-blue-800 transition-colors duration-200 mt-1"
    //                             >
    //                               <span className="truncate mr-1">{row[col.key]}</span>
    //                               <HiOutlineMail className="text-lg" />
    //                             </button>
    //                           ) : (
    //                             <span className="text-gray-600 mt-1">{row[col.key]}</span>
    //                           )}
    //                         </div>
    //                       ))}
    //                     </div>
    //                   </div>
    //                 )}
    //               </div>
    //             ))
    //           ) : (
    //             <div className="text-center text-gray-500">No data found</div>
    //           )}
    //         </div>
    //       </div>

    //       <div className="flex flex-col sm:flex-row justify-between items-center mt-4">
    //         <div className="text-sm text-gray-600 mb-4 sm:mb-0">
    //           Showing {startIndex + 1} to {Math.min(startIndex + entriesPerPage, filteredData.length)} of{" "}
    //           {filteredData.length} entries
    //         </div>

    //         {entriesPerPage !== "All" && (
    //           <div className="flex flex-wrap justify-center items-center gap-2">
    //             <button
    //               onClick={handlePrevPage}
    //               disabled={currentPage === 1}
    //               className="px-3 py-1 bg-gray-300 rounded-l hover:bg-gray-400 disabled:opacity-50 text-sm"
    //             >
    //               Previous
    //             </button>
    //             {[...Array(totalPages).keys()].map((page) => (
    //               <button
    //                 key={page + 1}
    //                 onClick={() => handlePageChange(page + 1)}
    //                 className={`px-3 py-1 border text-sm ${
    //                   currentPage === page + 1 ? "bg-blue-500 text-white" : "bg-gray-200 hover:bg-gray-300"
    //                 }`}
    //               >
    //                 {page + 1}
    //               </button>
    //             ))}
    //             <button
    //               onClick={handleNextPage}
    //               disabled={currentPage === totalPages}
    //               className="px-3 py-1 bg-gray-300 rounded-r hover:bg-gray-400 disabled:opacity-50 text-sm"
    //             >
    //               Next
    //             </button>
    //           </div>
    //         )}
    //       </div>
    //     </div>
    //   )}
    // </div>

    <div className="relative min-h-screen">
    {/* Loading Indicator */}
 
   {loading && (
        <div className="fixed inset-0 flex items-center justify-center z-50">
          <div className="flex flex-col items-center p-3 bg-black border border-white rounded-2xl">
            <FadeLoader 
              height={15}
              width={5}
              color="#ffffff"
              ariaLabel="loading"
            />
            <h1 className="mt-1 text-lg text-white">Please wait...</h1>
          </div>
        </div>
      )}
      
  
    {/* Page Content */}
    <div className="flex items-center justify-center min-h-screen">
      <div className="min-h-screen bg-white p-4 lg:px-8 xl:px-16 2xl:px-24">
        {successMessage && (
          <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded relative mb-4">
            <span className="block sm:inline">{successMessage}</span>
          </div>
        )}
  
        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative mb-4">
            <span className="block sm:inline">{error}</span>
          </div>
        )}
        <h1 className="text-2xl font-bold p-4 text-center sm:text-left">Client Payments Details</h1>
  
        <div className="flex flex-col space-y-4 sm:space-y-0 sm:flex-row sm:justify-between sm:items-start w-full">
          <div className="flex flex-col sm:flex-row items-center space-y-4 sm:space-y-0 sm:space-x-4">
            <div className="relative w-full sm:w-auto">
              <input
                type="text"
                placeholder="Search..."
                value={searchQuery}
                onChange={handleSearch}
                className="p-3 w-full sm:w-80 rounded-full border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 transition duration-300 pl-10"
              />
              <CiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500" />
            </div>
          </div>
          <div className="flex flex-col sm:flex-row items-center space-y-4 sm:space-y-0 sm:space-x-4">
            <div className="flex space-x-2">
              <button
                onClick={handleExcelExport}
                className="bg-gradient-to-r from-blue-500 to-blue-600 text-white font-semibold text-sm sm:text-base py-2 px-4 rounded-full shadow-lg hover:shadow-xl hover:from-blue-600 hover:to-blue-700 transition-all duration-300 transform hover:-translate-y-1 hover:scale-105 flex items-center gap-2"
              >
                <FaFileExcel className="text-xl" />
                Excel
              </button>
              <button
                onClick={handlePdfExport}
                className="bg-gradient-to-r from-red-500 to-red-600 text-white font-semibold text-sm sm:text-base py-2 px-4 rounded-full shadow-lg hover:shadow-xl hover:from-red-600 hover:to-red-700 transition-all duration-300 transform hover:-translate-y-1 hover:scale-105 flex items-center gap-2"
              >
                <FaFilePdf className="text-xl" />
                PDF
              </button>
            </div>
            <div className="flex items-center space-x-2">
              <span className="text-sm">Show</span>
              <select
                value={entriesPerPage}
                onChange={handleEntriesChange}
                className="p-2 rounded-xl border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value="All">All</option>
              </select>
              <span className="text-sm">entries</span>
            </div>
          </div>
        </div>
  
        <div className="overflow-x-auto mt-6">
          <table className="w-full table-auto bg-white border border-gray-300 shadow-md rounded-lg hidden lg:table">
            <thead className="bg-blue-100 text-left">
              <tr>
                <th className="px-4 py-2 border-b w-16">
                  <span>#</span>
                </th>
                {visibleColumns.map((col) => (
                  <th
                    key={col?.key}
                    className="px-4 py-2 border-b cursor-pointer min-w-[120px]"
                    onClick={() => handleSort(col?.key)}
                  >
                    <div className="flex items-center gap-2">
                      {col?.label} {getSortIcon(col?.key)}
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {paginatedData.length > 0 ? (
                paginatedData.map((row, index) => (
                  <>
                    <tr key={row.order_id} className="hover:bg-gray-50 transition-colors duration-200">
                      <td className="px-4 py-2 border-b">
                        <button
                          onClick={() => toggleRow(row.order_id)}
                          className="text-blue-600 hover:text-blue-800 transition-colors duration-200"
                        >
                          {expandedRows[row.order_id] ? <FaMinus /> : <FaPlus />}
                        </button>
                        <span className="ml-2">{startIndex + index + 1}</span>
                      </td>
                      {visibleColumns.map((col) => (
                        <td key={col.key} className="px-4 py-2 border-b">
                          {col.key === "payment_status" ? (
                            <div className="flex items-center gap-2">
                              <span
                                className={`px-2 py-1 rounded-full text-xs font-semibold ${
                                  row[col.key] === "Paid"
                                    ? "bg-green-100 text-green-700"
                                    : "bg-yellow-100 text-yellow-700"
                                }`}
                              >
                                {row[col.key]}
                              </span>
                              {row[col.key] === "Pending" && (
                                <button
                                  className="px-2 py-1 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors duration-200"
                                  onClick={() => toggleStatus(row.order_id)}
                                >
                                  Mark Paid
                                </button>
                              )}
                            </div>
                          ) : col.key === "client_email" ? (
                            row.payment_status === "Complete" ? (
                              <button
                                onClick={() => sendMail(row.order_id)}
                                className="flex items-center text-blue-600 hover:text-blue-800 transition-colors duration-200 mt-1"
                              >
                                <span className="truncate mr-1">{row[col.key]}</span>
                                <HiOutlineMail className="text-lg" />
                              </button>
                            ) : (
                              <span className="text-gray-400 italic">Not eligible – Please complete payment</span>
                            )
                          ) : (
                            <span className="truncate">{row[col.key]}</span>
                          )}
                        </td>
                      ))}
                    </tr>
                    {expandedRows[row.order_id] && (
                      <tr className="bg-gray-50">
                        <td
                          colSpan={visibleColumns.length + 1}
                          className="px-6 py-4 border-b transition-all duration-300 ease-in-out"
                        >
                          <div className="bg-white rounded-lg shadow-sm p-4 border border-gray-200">
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                              {hiddenColumns.map((col) => (
                                <div key={col.key} className="flex flex-col">
                                  <span className="text-sm font-semibold text-gray-700">{col.label}</span>
                                  {col.key === "payment_status" ? (
                                    <div className="flex items-center gap-2 mt-1">
                                      <span
                                        className={`px-2 py-1 rounded-full text-xs font-semibold ${
                                          row[col.key] === "Paid"
                                            ? "bg-green-100 text-green-700"
                                            : "bg-yellow-100 text-yellow-700"
                                        }`}
                                      >
                                        {row[col.key]}
                                      </span>
                                      {row[col.key] === "Pending" && (
                                        <button
                                          className="px-2 py-1 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors duration-200"
                                          onClick={() => toggleStatus(row.order_id)}
                                        >
                                          Mark Paid
                                        </button>
                                      )}
                                    </div>
                                  ) : col.key === "client_email" ? (
                                    row.payment_status === "Complete" ? (
                                      <button
                                        onClick={() => sendMail(row.order_id)}
                                        className="flex items-center text-blue-600 hover:text-blue-800 transition-colors duration-200 mt-1"
                                      >
                                        <span className="truncate mr-1">{row[col.key]}</span>
                                        <HiOutlineMail className="text-lg" />
                                      </button>
                                    ) : (
                                      <span className="text-gray-400 italic">Not eligible – Please complete payment</span>
                                    )
                                  ) : (
                                    <span className="text-gray-600 mt-1">{row[col.key]}</span>
                                  )}
                                </div>
                              ))}
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </>
                ))
              ) : (
                <tr>
                  <td colSpan={visibleColumns.length + 1} className="text-center py-4 text-gray-500">
                    No data available
                  </td>
                </tr>
              )}
            </tbody>
          </table>
  
          {/* Mobile View */}
          <div className="lg:hidden">
            {paginatedData.length > 0 ? (
              paginatedData.map((row, index) => (
                <div
                  key={row.order_id}
                  className="bg-white border border-gray-300 shadow-md rounded-lg mb-4 p-4"
                >
                  <div className="flex justify-between items-center mb-3">
                    <div className="font-bold text-lg flex items-center">
                      <button
                        onClick={() => toggleRow(row.order_id)}
                        className="text-blue-600 hover:text-blue-800 mr-2 transition-colors duration-200"
                      >
                        {expandedRows[row.order_id] ? <FaMinus /> : <FaPlus />}
                      </button>
                      Order ID: {row.order_id}
                    </div>
                    <span className="text-gray-500">{row.payment_method}</span>
                  </div>
                  <div className="grid grid-cols-1 gap-3">
                    {visibleColumns.slice(1).map((col) => (
                      <div key={col.key} className="flex flex-col">
                        <span className="font-semibold text-gray-700">{col.label}:</span>
                        <span className="text-gray-600">{row[col.key]}</span>
                      </div>
                    ))}
                  </div>
                  {expandedRows[row.order_id] && (
                    <div className="mt-4 bg-gray-50 p-4 rounded-lg shadow-inner">
                      <div className="grid grid-cols-1 gap-3">
                        {hiddenColumns.map((col) => (
                          <div key={col.key} className="flex flex-col">
                            <span className="font-semibold text-gray-700">{col.label}:</span>
                            {col.key === "payment_status" ? (
                              <div className="flex items-center gap-2 mt-1">
                                <span
                                  className={`px-2 py-1 rounded-full text-xs font-semibold ${
                                    row[col.key] === "Paid"
                                      ? "bg-green-100 text-green-700"
                                      : "bg-yellow-100 text-yellow-700"
                                  }`}
                                >
                                  {row[col.key]}
                                </span>
                                {row[col.key] === "Pending" && (
                                  <button
                                    className="px-2 py-1 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors duration-200"
                                    onClick={() => toggleStatus(row.order_id)}
                                  >
                                    Mark Paid
                                  </button>
                                )}
                              </div>
                            ) : col.key === "client_email" ? (
                              row.payment_status === "Complete" ? (
                                <button
                                  onClick={() => sendMail(row.order_id)}
                                  className="flex items-center text-blue-600 hover:text-blue-800 transition-colors duration-200 mt-1"
                                >
                                  <span className="truncate mr-1">{row[col.key]}</span>
                                  <HiOutlineMail className="text-lg" />
                                </button>
                              ) : (
                                <span className="text-gray-400 italic">Not eligible – Please complete payment </span>
                              )
                            ) : (
                              <span className="text-gray-600 mt-1">{row[col.key]}</span>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))
            ) : (
              <div className="text-center text-gray-500">No data found</div>
            )}
          </div>
        </div>
  
        <div className="flex flex-col sm:flex-row justify-between items-center mt-4">
          <div className="text-sm text-gray-600 mb-4 sm:mb-0">
            Showing {startIndex + 1} to {Math.min(startIndex + entriesPerPage, filteredData.length)} of{" "}
            {filteredData.length} entries
          </div>
  
          {entriesPerPage !== "All" && (
            <div className="flex flex-wrap justify-center items-center gap-2">
              <button
                onClick={handlePrevPage}
                disabled={currentPage === 1}
                className="px-3 py-1 bg-gray-300 rounded-l hover:bg-gray-400 disabled:opacity-50 text-sm"
              >
                Previous
              </button>
              {[...Array(totalPages).keys()].map((page) => (
                <button
                  key={page + 1}
                  onClick={() => handlePageChange(page + 1)}
                  className={`px-3 py-1 border text-sm ${
                    currentPage === page + 1 ? "bg-blue-500 text-white" : "bg-gray-200 hover:bg-gray-300"
                  }`}
                >
                  {page + 1}
                </button>
              ))}
              <button
                onClick={handleNextPage}
                disabled={currentPage === totalPages}
                className="px-3 py-1 bg-gray-300 rounded-r hover:bg-gray-400 disabled:opacity-50 text-sm"
              >
                Next
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  </div>
  );
}

export default Client_pay_1;







// import { useState, useEffect, useCallback } from "react"
// import { PiSortAscendingLight, PiSortDescendingLight } from "react-icons/pi"
// import { CiSearch } from "react-icons/ci"
// import "react-datepicker/dist/react-datepicker.css"
// import { format, subDays, subMonths, startOfMonth, endOfMonth } from "date-fns"
// import { FaFileExcel, FaFilePdf } from "react-icons/fa"
// import { HiOutlineMail } from "react-icons/hi"
// import { jsPDF } from "jspdf"
// import * as XLSX from "xlsx"
// import "jspdf-autotable"
// import axios from "axios"
// import emailjs from "@emailjs/browser"
// import { BaseUrl } from "../App"
// import { Circles } from 'react-loader-spinner';

// function Client_pay_1() {
//   const [selectedOption, setSelectedOption] = useState("Today")
//   const [startDate, setStartDate] = useState(new Date())
//   const [endDate, setEndDate] = useState(new Date())
//   const [showCalendars, setShowCalendars] = useState(false)
//   const [successMessage, setSuccessMessage] = useState("")
// //  const [loading, setLoading] = useState(false);

//   const [tableData, setTableData] = useState([])
//   const [sortConfig, setSortConfig] = useState({ key: "", direction: "" })
//   const [searchQuery, setSearchQuery] = useState("")
//   const [entriesPerPage, setEntriesPerPage] = useState(10)
//   const [currentPage, setCurrentPage] = useState(1)
//   const [loading, setLoading] = useState(true)
//   const [error, setError] = useState(null)

//   const handleSelect = (option) => {
//     setSelectedOption(option)
//     let start, end
//     const today = new Date()

//     switch (option) {
//       case "Today":
//         start = end = today
//         break
//       case "Yesterday":
//         start = end = subDays(today, 1)
//         break
//       case "Last 7 Days":
//         start = subDays(today, 7)
//         end = today
//         break
//       case "Last 30 Days":
//         start = subDays(today, 30)
//         end = today
//         break
//       case "This Month":
//         start = startOfMonth(today)
//         end = endOfMonth(today)
//         break
//       case "Last Month":
//         start = startOfMonth(subMonths(today, 1))
//         end = endOfMonth(subMonths(today, 1))
//         break
//       case "Custom Range":
//         setShowCalendars(true)
//         return
//       default:
//         start = end = today
//     }

//     setStartDate(start)
//     setEndDate(end)
//     setShowCalendars(false)
//   }

//   const fetchData = useCallback(
//     async (retryCount = 0) => {
//       try {
//         setLoading(true)
//         setError(null)
//         const response = await axios.post(`${BaseUrl}/backend/api/payment_by_user_type.php`, {
//           action: "get_all_payments",
//           api_key: "1",
//           page: currentPage - 1,
//         })

//         if (response.data.status === "true" && Array.isArray(response.data.response)) {
//           setTableData(response.data.response)
//         } else {
//           setError("No payments available or invalid response format")
//           setTableData([])
//         }
//       } catch (err) {
//         console.error("Error fetching data:", err)
//         if (retryCount < 3) {
//           console.log(`Retrying... Attempt ${retryCount + 1}`)
//           setTimeout(() => fetchData(retryCount + 1), 1000 * (retryCount + 1))
//         } else {
//           setError("Network error. Please check your connection and try again.")
//           setTableData([])
//         }
//       } finally {
//         setLoading(false)
//       }
//     },
//     [currentPage],
//   )

//   const handleSort = (column) => {
//     let direction = "asc"
//     if (sortConfig.key === column && sortConfig.direction === "asc") {
//       direction = "desc"
//     }
//     setSortConfig({ key: column, direction })

//     const sortedData = [...tableData].sort((a, b) => {
//       if (a[column] < b[column]) return direction === "asc" ? -1 : 1
//       if (a[column] > b[column]) return direction === "asc" ? 1 : -1
//       return 0
//     })

//     setTableData(sortedData)
//   }

//   const getSortIcon = (column) => {
//     if (sortConfig.key !== column) {
//       return <PiSortAscendingLight className="text-gray-500" />
//     }
//     return sortConfig.direction === "asc" ? <PiSortAscendingLight /> : <PiSortDescendingLight />
//   }

//   const toggleStatus = async (orderId) => {
//     setLoading(true);
//     try {
//       const currentStatus = tableData.find((booking) => booking.order_id === orderId)?.payment_status

//       if (!currentStatus) {
//         throw new Error("Booking not found")
//       }

//       const newStatus = currentStatus === "Paid" ? "Pending" : "Paid"

//       const response = await axios.post(`${BaseUrl}/backend/api/payment_by_user_type.php`, {
//         action: "update_payment_status",
//         api_key: "1",
//         order_id: orderId,
//         status: newStatus,
//       })

//       if (response.data && response.data.status === "true") {
//         setSuccessMessage("Payment Status Change Successfully!")

//         setTableData((prevData) =>
//           prevData.map((booking) =>
//             booking.order_id === orderId ? { ...booking, payment_status: newStatus } : booking,
//           ),
//         )
//         alert(`Payment status updated successfully to ${newStatus}`)
//       } else {
//         throw new Error(response.data?.response || "Failed to update payment status")
//       }
//     } catch (error) {
//       console.error("Error updating payment status:", error)
//       let errorMessage = "An unexpected error occurred"
//       if (error.response) {
//         errorMessage = `Server error: ${error.response.data.message || error.response.statusText}`
//       } else if (error.request) {
//         errorMessage = "No response received from server"
//       } else {
//         errorMessage = error.message
//       }
//       alert(`Failed to update payment status: ${errorMessage}`)
//     } finally {
//       setLoading(false);
//     }
//   }

//   const handleSearch = (e) => setSearchQuery(e.target.value)

//   const handleEntriesChange = (e) => {
//     setEntriesPerPage(e.target.value === "All" ? "All" : Number.parseInt(e.target.value, 10))
//     setCurrentPage(1)
//   }

//   const filteredData = tableData?.filter((row) =>
//     Object.values(row).some((value) => value?.toString()?.toLowerCase().includes(searchQuery.toLowerCase())),
//   )

//   const startIndex = (currentPage - 1) * entriesPerPage
//   const paginatedData =
//     entriesPerPage === "All" ? filteredData : filteredData.slice(startIndex, startIndex + entriesPerPage)

//     console.log(paginatedData)

//   const totalPages = Math.ceil(filteredData.length / entriesPerPage)

//   const handlePrevPage = () => setCurrentPage((prev) => Math.max(prev - 1, 1))
//   const handleNextPage = () => setCurrentPage((prev) => Math.min(prev + 1, totalPages))
//   const handlePageChange = (page) => {
//     setCurrentPage(page)
//     fetchData()
//   }

//   const handleExcelExport = () => {
//     const columns = [
//       "S.No",
//       "Order ID",
//       "Client Name",
//       "Payment Method",
//       "Transaction ID",
//       "Payment Date",
//       "Amount",
//       "Discount",
//       "Taxes",
//       "Net Amount",
//       "Partial Amount",
//       "Recurrence",
//       "Status",
//     ]

//     const rows = tableData.map((row) => [
//       row.id,
//       row.order_id,
//       row.client_name,
//       row.payment_method,
//       row.transaction_id,
//       row.payment_date,
//       row.amount,
//       row.discount,
//       row.taxes,
//       row.net_amount,
//       row.partial_amount,
//       row.recurrence_status,
//       row.payment_status,
//     ])

//     const ws = XLSX.utils.aoa_to_sheet([columns, ...rows])
//     const wb = XLSX.utils.book_new()
//     XLSX.utils.book_append_sheet(wb, ws, "Table Data")
//     XLSX.writeFile(wb, "PaymentDetail.xlsx")
//   }

//   const handlePdfExport = () => {
//     const doc = new jsPDF()
//     const columns = [
//       "S.No",
//       "Order ID",
//       "Client Name",
//       "Payment Method",
//       "Transaction ID",
//       "Payment Date",
//       "Amount",
//       "Discount",
//       "Taxes",
//       "Net Amount",
//       "Partial Amount",
//       "Recurrence",
//       "Status",
//     ]
//     const rows = tableData.map((row) => [
//        row.id,
//       row.order_id,
//       row.client_name,
//       row.payment_method,
//       row.transaction_id,
//       row.payment_date,
//       row.amount,
//       row.discount,
//       row.taxes,
//       row.net_amount,
//       row.partial_amount,
//       row.recurrence_status,
//       row.payment_status,
//     ])

//     doc.autoTable({
//       head: [columns],
//       body: rows,
//       startY: 20,
//       theme: "grid",
//       margin: { horizontal: 10 },
//       headStyles: {
//         fillColor: [0, 123, 255],
//         textColor: 255,
//       },
//     })

//     doc.save("PaymentDetail.pdf")
//   }

//   useEffect(() => {
//     fetchData()
//   }, [fetchData])

//   const sendMail = async (clientEmail, clientName) => {
//     console.log(clientEmail, clientName)
//     try {
//       const response = await emailjs.send(
//         "service_jyesga5",
//         "template_272d2yt",
//         {
//           to_email: clientEmail,
//           to_name: clientName,
//           message: `Hello ${clientName}, this is your payment confirmation email.`,
//         },
//         "iFU9xRbx6uWKc2Kbg",
//       )
//       alert("Email sent successfully!")
//       console.log("Success:", response)
//     } catch (error) {
//       alert("Failed to send email.")
//       console.error("Error sending email:", error)
//     }
//   }

//   return (
//       <div className="flex items-center justify-center min-h-screen ">
//     {loading ? (
//       <>
//       <Circles
//         height="80"
//         width="80"
//         color="grey"
//         ariaLabel="loading"
//       /><br></br>
//     <h1>Please wait...</h1>
       
//       </>
//     ) : (
//     <div className="min-h-screen bg-white p-4 lg:px-8 xl:px-16 2xl:px-24">
//       {successMessage && (
//         <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded relative mb-4">
//           <span className="block sm:inline">{successMessage}</span>
//         </div>
//       )}

//       {error && (
//         <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative mb-4">
//           <span className="block sm:inline">{error}</span>
//         </div>
//       )}
//       <h1 className="text-2xl font-bold p-4 text-center sm:text-left">Client Payments Details</h1>

//       <div className="flex flex-col space-y-4 sm:space-y-0 sm:flex-row sm:justify-between sm:items-start w-full">
//         <div className="flex flex-col sm:flex-row items-center space-y-4 sm:space-y-0 sm:space-x-4">
//           <div className="relative w-full sm:w-auto">
//             <input
//               type="text"
//               placeholder="Search..."
//               value={searchQuery}
//               onChange={handleSearch}
//               className="p-3 w-full sm:w-80 rounded-full border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 transition duration-300 pl-10"
//             />
//             <CiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500" />
//           </div>
         
//         </div>
//         <div className="flex flex-col sm:flex-row items-center space-y-4 sm:space-y-0 sm:space-x-4">
//           <div className="flex space-x-2">
//             <button
//               onClick={handleExcelExport}
//               className="bg-gradient-to-r from-blue-500 to-blue-600 text-white font-semibold text-sm sm:text-base py-2 px-4 rounded-full shadow-lg hover:shadow-xl hover:from-blue-600 hover:to-blue-700 transition-all duration-300 transform hover:-translate-y-1 hover:scale-105 flex items-center gap-2"
//             >
//               <FaFileExcel className="text-xl" />
//               Excel
//             </button>
//             <button
//               onClick={handlePdfExport}
//               className="bg-gradient-to-r from-red-500 to-red-600 text-white font-semibold text-sm sm:text-base py-2 px-4 rounded-full shadow-lg hover:shadow-xl hover:from-red-600 hover:to-red-700 transition-all duration-300 transform hover:-translate-y-1 hover:scale-105 flex items-center gap-2"
//             >
//               <FaFilePdf className="text-xl" />
//               PDF
//             </button>
//           </div>
//           <div className="flex items-center space-x-2">
//             <span className="text-sm">Show</span>
//             <select
//               value={entriesPerPage}
//               onChange={handleEntriesChange}
//               className="p-2 rounded-xl border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
//             >
//               <option value={10}>10</option>
//               <option value={25}>25</option>
//               <option value={50}>50</option>
//               <option value="All">All</option>
//             </select>
//             <span className="text-sm">entries</span>
//           </div>
//         </div>
//       </div>

//       <div className="overflow-x-auto mt-6">
//         <table className="min-w-full table-auto bg-white border border-gray-300 shadow-md rounded-lg hidden lg:table">
//           <thead className="bg-blue-100 text-left">
//             <tr>
//               <th className="px-4 py-2 border-b cursor-pointer">#</th>
//               <th className="px-4 py-2 border-b cursor-pointer" onClick={() => handleSort("order_id")}>
//                 <div className="flex items-center gap-2">Order ID {getSortIcon("order_id")}</div>
//               </th>
//               <th className="px-4 py-2 border-b cursor-pointer" onClick={() => handleSort("client_name")}>
//                 <div className="flex items-center gap-2">Client Name {getSortIcon("client_name")}</div>
//               </th>
//               <th className="px-4 py-2 border-b cursor-pointer" onClick={() => handleSort("payment_method")}>
//                 <div className="flex items-center gap-2">Payment Method {getSortIcon("payment_method")}</div>
//               </th>
//               {/* <th className="px-4 py-2 border-b cursor-pointer" onClick={() => handleSort("transaction_id")}>
//                 <div className="flex items-center gap-2">Transaction ID {getSortIcon("transaction_id")}</div>
//               </th> */}
//               <th className="px-4 py-2 border-b cursor-pointer" onClick={() => handleSort("payment_date")}>
//                 <div className="flex items-center gap-2">Payment Date {getSortIcon("payment_date")}</div>
//               </th>
//               <th className="px-4 py-2 border-b cursor-pointer" onClick={() => handleSort("amount")}>
//                 <div className="flex items-center gap-2">Amount {getSortIcon("amount")}</div>
//               </th>
//               <th className="px-4 py-2 border-b cursor-pointer" onClick={() => handleSort("discount")}>
//                 <div className="flex items-center gap-2">Discount {getSortIcon("discount")}</div>
//               </th>
//               <th className="px-4 py-2 border-b cursor-pointer" onClick={() => handleSort("taxes")}>
//                 <div className="flex items-center gap-2">Taxes {getSortIcon("taxes")}</div>
//               </th>
//               <th className="px-4 py-2 border-b cursor-pointer" onClick={() => handleSort("net_amount")}>
//                 <div className="flex items-center gap-2">Net Amount {getSortIcon("net_amount")}</div>
//               </th>
//               <th className="px-4 py-2 border-b cursor-pointer" onClick={() => handleSort("Partial_amount")}>
//                 <div className="flex items-center gap-2">Partial_amount {getSortIcon("Partial_amount")}</div>
//               </th>
//               <th className="px-4 py-2 border-b cursor-pointer" onClick={() => handleSort("Recurrence")}>
//                 <div className="flex items-center gap-2">Recurrence {getSortIcon("Recurrence")}</div>
//               </th>
//               <th className="px-4 py-2 border-b cursor-pointer" onClick={() => handleSort("Status")}>
//                 <div className="flex items-center gap-2">Status {getSortIcon("Status")}</div>
//               </th>
//               <th className="px-4 py-2 border-b cursor-pointer" onClick={() => handleSort("Email")}>
//                 <div className="flex items-center gap-2">Email {getSortIcon("Email")}</div>
//               </th>
//             </tr>
//           </thead>
//           <tbody>
//             {paginatedData.length > 0 ? (
//               paginatedData.map((row, index) => (
//                 <tr key={row.order_id} className="hover:bg-gray-50">
//                   <td className="px-4 py-2 border-b">{startIndex + index + 1}</td>
//                   <td className="px-4 py-2 border-b">{row.order_id}</td>
//                   <td className="px-4 py-2 border-b">{row.client_name}</td>
//                   <td className="px-4 py-2 border-b">{row.payment_method}</td>
//                   {/* <td className="px-4 py-2 border-b">{row.transaction_id}</td> */}
//                   <td className="px-4 py-2 border-b">{row.payment_date}</td>
//                   <td className="px-4 py-2 border-b">{row.amount}</td>
//                   <td className="px-4 py-2 border-b">{row.discount}</td>
//                   <td className="px-4 py-2 border-b">{row.taxes}</td>
//                   <td className="px-4 py-2 border-b">{row.net_amount}</td>
//                   <td className="px-4 py-2 border-b">{row.partial_amount}</td>
//                   <td className="px-4 py-2 border-b">{row.recurrence_status}</td>
//                   {/* <td className="px-4 py-2 border-b">
//                     {row.payment_status}
//                     <button
//                       className={`ml-2 px-2 py-1 bg-blue-500 text-white rounded ${row.Status === "Pending" ? "bg-green-500" : "bg-blue-500"}`}
//                       onClick={() => toggleStatus(row.order_id)}
//                     >
//                       {row.payment_status === "Pending" ? " Paid" : "Pending"}
//                     </button>
//                   </td> */}
//                   <td className="px-4 py-2 border-b"> 
//   {row.payment_status}
  
//   {row.payment_status === "Pending" && (
//     <button
//       className="ml-2 px-2 py-1 bg-green-500 text-white rounded"
//       onClick={() => toggleStatus(row.order_id)}
//     >
//       Paid
//     </button>
//   )}
// </td>

//                   <td className="p-4 border-b hover:bg-gray-50 transition-colors duration-200">
//                     <button
//                       onClick={() => sendMail(row.client_email, row.client_name)}
//                       className="w-full group flex items-center gap-3 text-left focus:outline-none focus:ring-2 focus:ring-blue-500 rounded-lg p-2"
//                     >
//                       <div className="flex-1 min-w-0">
//                         <span className="block text-sm font-medium text-gray-900 truncate">{row.client_email}</span>
//                         <span className="inline-flex items-center text-sm text-blue-600 group-hover:text-blue-800 transition-colors duration-200">
//                           Send mail
//                           <HiOutlineMail className="ml-2 h-5 w-5" />
//                         </span>
//                       </div>
//                     </button>
//                   </td>
//                 </tr>
//               ))
//             ) : (
//               <tr>
//                 <td colSpan="14" className="text-center py-4">
//                   No data available
//                 </td>
//               </tr>
//             )}
//           </tbody>
//         </table>

//         <div className="lg:hidden">
//           {paginatedData.length > 0 ? (
//             paginatedData.map((row, index) => (
//               <div key={row.order_id} className="bg-white border border-gray-300 shadow-md rounded-lg mb-4 p-4">
//                 <div className="flex justify-between items-center mb-3">
//                   <div className="font-bold text-lg">Order ID: {row.order_id}</div>
//                   <div className="text-gray-500">{row.payment_method}</div>
//                 </div>
//                 <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
//                   <div>
//                     <span className="font-semibold">Client Name: </span>
//                     {row.client_name}
//                   </div>
//                   <div>
//                     <span className="font-semibold">Transaction ID: </span>
//                     {row.transaction_id}
//                   </div>
//                   <div>
//                     <span className="font-semibold">Payment Date: </span>
//                     {row.payment_date}
//                   </div>
//                   <div>
//                     <span className="font-semibold">Amount: </span>
//                     {row.amount}
//                   </div>
//                   <div>
//                     <span className="font-semibold">Discount: </span>
//                     {row.discount}
//                   </div>
//                   <div>
//                     <span className="font-semibold">Taxes: </span>
//                     {row.taxes}
//                   </div>
//                   <div>
//                     <span className="font-semibold">Net Amount: </span>
//                     {row.net_amount}
//                   </div>
//                   <div>
//                     <span className="font-semibold">Partial Amount: </span>
//                     {row.partial_amount}
//                   </div>
//                   <div>
//                     <span className="font-semibold">Recurrence: </span>
//                     {row.recurrence_status}
//                   </div>
//                   <div>
//                     <span className="font-semibold">Status: </span> {row.payment_status}
//                     <button
//                       className={`ml-2 px-2 py-1 text-white rounded ${row.payment_status === "Pending" ? "bg-green-500" : "bg-blue-500"}`}
//                       onClick={() => toggleStatus(row.order_id)}
//                     >
//                       {row.payment_status === "Pending" ? "Paid" : "Pending"}
//                     </button>
//                   </div>
//                 </div>
//                 <div className="mt-3">
//                   <span className="font-semibold">Email: </span>
//                   <button
//                     onClick={() => sendMail(row.client_email, row.client_name)}
//                     className="flex items-center text-blue-600 hover:text-blue-800"
//                   >
//                     <span className="mr-1">{row.client_email}</span>
//                     <HiOutlineMail className="text-xl" />
//                   </button>
//                 </div>
//               </div>
//             ))
//           ) : (
//             <div className="text-center text-gray-500">No data found</div>
//           )}
//         </div>
//       </div>

//       <div className="flex flex-col sm:flex-row justify-between items-center mt-4">
//         <div className="text-sm text-gray-600 mb-4 sm:mb-0">
//           Showing {startIndex + 1} to {Math.min(startIndex + entriesPerPage, filteredData.length)} of{" "}
//           {filteredData.length} entries
//         </div>

//         {entriesPerPage !== "All" && (
//           <div className="flex flex-wrap justify-center items-center gap-2">
//             <button
//               onClick={handlePrevPage}
//               disabled={currentPage === 1}
//               className="px-3 py-1 bg-gray-300 rounded-l hover:bg-gray-400 disabled:opacity-50 text-sm"
//             >
//               Previous
//             </button>
//             {[...Array(totalPages).keys()].map((page) => (
//               <button
//                 key={page + 1}
//                 onClick={() => handlePageChange(page + 1)}
//                 className={`px-3 py-1 border text-sm ${
//                   currentPage === page + 1 ? "bg-blue-500 text-white" : "bg-gray-200 hover:bg-gray-300"
//                 }`}
//               >
//                 {page + 1}
//               </button>
//             ))}
//             <button
//               onClick={handleNextPage}
//               disabled={currentPage === totalPages}
//               className="px-3 py-1 bg-gray-300 rounded-r hover:bg-gray-400 disabled:opacity-50 text-sm"
//             >
//               Next
//             </button>
//           </div>
//         )}
//       </div>
//       {loading && <div className="text-center mt-4">Loading...</div>}
//       {error && (
//         <div className="text-center mt-4">
//           <p className="text-red-500">{error}</p>
//           <button
//             onClick={() => fetchData()}
//             className="mt-2 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
//           >
//             Retry
//           </button>
//         </div>
//       )}
//     </div>
//      )}
//   </div>
//   )
// }

// export default Client_pay_1
